import type { DailySession, ProfileId, RelaySession } from "./types";

const DB_NAME = "duomatt-offline";
const DB_VERSION = 1;

export type OutboxJob =
  | {
      id: string;
      kind: "session_answer";
      profileId: ProfileId;
      body: {
        session_id: string;
        item_id: string;
        user_answer: string;
        request_id: string;
      };
      createdAt: number;
    }
  | {
      id: string;
      kind: "session_complete";
      profileId: ProfileId;
      body: { session_id: string };
      createdAt: number;
    }
  | {
      id: string;
      kind: "relay_answer";
      profileId: ProfileId;
      body: {
        date_key: string;
        request_id: string;
        item_index: number;
        attempt: 0 | 1;
        user_answer: string;
      };
      createdAt: number;
    }
  | {
      id: string;
      kind: "relay_complete";
      profileId: ProfileId;
      body: { date_key: string };
      createdAt: number;
    };

interface CacheEntry {
  key: string;
  value: unknown;
  savedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("outbox")) {
        db.createObjectStore("outbox", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("cache")) {
        db.createObjectStore("cache", { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transaction<T>(
  storeName: "outbox" | "cache",
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const request = work(tx.objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
    tx.onerror = () => reject(tx.error);
  }));
}

export function enqueue(job: OutboxJob): Promise<void> {
  return transaction("outbox", "readwrite", (store) => store.put(job)).then(() => undefined);
}

export function outboxJobs(): Promise<OutboxJob[]> {
  return transaction("outbox", "readonly", (store) => store.getAll())
    .then((jobs) => jobs.sort((a, b) => a.createdAt - b.createdAt));
}

export function removeOutboxJob(id: string): Promise<void> {
  return transaction("outbox", "readwrite", (store) => store.delete(id)).then(() => undefined);
}

export function cacheSet(key: string, value: unknown): Promise<void> {
  const entry: CacheEntry = { key, value, savedAt: Date.now() };
  return transaction("cache", "readwrite", (store) => store.put(entry)).then(() => undefined);
}

export function cacheGet<T>(key: string): Promise<T | null> {
  return transaction("cache", "readonly", (store) => store.get(key))
    .then((entry) => (entry as CacheEntry | undefined)?.value as T ?? null);
}

export const sessionCacheKey = (profile: ProfileId, game: string, date: string) =>
  `session:${profile}:${game}:${date}`;
export const relayCacheKey = (profile: ProfileId, date: string) =>
  `relay:${profile}:${date}`;

export type CachedSession = DailySession;
export type CachedRelay = RelaySession;
